import { FormEvent, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { fetchLegalPages, subscribeNewsletter, type LegalPageMeta } from '@/lib/api-client'
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher'
import { useLanguage } from '@/lib/language-context'
import { useTranslation } from '@/lib/use-translation'
import { Facebook, Instagram, Linkedin } from 'lucide-react'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function SiteFooter() {
    const location = useLocation()
    const { locale } = useLanguage()
    const { t } = useTranslation()
    const [email, setEmail] = useState('')
    const [legalPages, setLegalPages] = useState<LegalPageMeta[]>([])
    const [legalLoading, setLegalLoading] = useState(false)
    const [subscribing, setSubscribing] = useState(false)
    const [subscribeSuccess, setSubscribeSuccess] = useState(false)
    const [subscribeError, setSubscribeError] = useState<string | null>(null)
    const [emailValidationError, setEmailValidationError] = useState<string | null>(null)
    const isAffiliatePage = location.pathname === '/affiliate'

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        if (!email.trim() || subscribing) return

        setEmailValidationError(null)
        setSubscribeError(null)
        if (!EMAIL_REGEX.test(email.trim())) {
            setEmailValidationError(t('footer.invalidEmail'))
            return
        }

        setSubscribing(true)
        setSubscribeError(null)
        setSubscribeSuccess(false)

        try {
            await subscribeNewsletter(email.trim(), locale)
            setSubscribeSuccess(true)
            setEmail('')
            // Hide success message after 3 seconds
            setTimeout(() => {
                setSubscribeSuccess(false)
            }, 3000)
        } catch (err: unknown) {
            setSubscribeError(err instanceof Error ? err.message : 'Failed to subscribe to newsletter')
            // Hide error message after 5 seconds
            setTimeout(() => {
                setSubscribeError(null)
            }, 5000)
        } finally {
            setSubscribing(false)
        }
    }

    useEffect(() => {
        let active = true
        async function loadLegalPages() {
            try {
                setLegalLoading(true)
                const pages = await fetchLegalPages(locale)
                if (active) {
                    setLegalPages(pages)
                }
            } catch (error) {
                console.error('Failed to fetch legal pages', error)
            } finally {
                if (active) setLegalLoading(false)
            }
        }

        loadLegalPages()
        return () => {
            active = false
        }
    }, [locale])

    return (
        <footer className="!border-t !border-slate-100 !bg-white">
            {/* CTA strip */}
            {isAffiliatePage ? (
                <div className="!bg-gradient-to-r !from-[#4568DC] !via-[#6A7BFF] !to-[#B06AB3]">
                    <div className="!mx-auto !flex !max-w-[1680px] !flex-col !gap-6 !px-5 !py-12 sm:!px-10 md:!px-10 lg:!px-16 xl:!px-20 md:!flex-row md:!items-center md:!gap-8">
                        <div className="!w-full !text-white md:!w-1/2 md:!min-w-0">
                            <p className="!text-xs !font-semibold !uppercase !tracking-[0.18em] !text-white/75">
                                {t('affiliate.finalCta.eyebrow')}
                            </p>
                            <h2 className="!mt-2 !text-2xl font-heading font-semibold sm:!text-3xl md:!text-4xl">
                                {t('affiliate.finalCta.title')}
                            </h2>
                            <p className="!mt-3 !max-w-2xl !text-sm !text-white/80 sm:!text-base">
                                {t('affiliate.finalCta.subtitle')}
                            </p>
                        </div>

                        <div className="!flex !w-full !flex-col !gap-3 sm:!flex-row sm:!items-center sm:!justify-start md:!w-1/2 md:!justify-end">
                            <Button
                                asChild
                                className="!h-11 !rounded-full !bg-white !px-6 !text-sm font-semibold !text-[#4568DC] hover:!bg-slate-100 sm:!w-auto"
                            >
                                <Link to="/contact?category=partnerships&topic=affiliate-program">
                                    {t('affiliate.finalCta.ctaAffiliate')} →
                                </Link>
                            </Button>
                            <Button
                                asChild
                                variant="outline"
                                className="!h-11 !rounded-full !border !border-white/25 !bg-white/12 !px-6 !text-sm font-semibold !text-white !backdrop-blur-[20px] hover:!bg-white/20 sm:!w-auto"
                            >
                                <Link to="/contact?category=partnerships&topic=student-ambassador">
                                    {t('affiliate.finalCta.ctaAmbassador')} →
                                </Link>
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="!bg-gradient-to-r !from-[#4568DC] !via-[#6A7BFF] !to-[#B06AB3]">
                    <div className="!mx-auto !flex !max-w-[1680px] !flex-col !gap-6 !px-5 !py-12 sm:!px-10 md:!px-10 lg:!px-16 xl:!px-20 md:!flex-row md:!items-center md:!gap-8">
                        <div className="!w-full !text-white md:!w-1/2 md:!min-w-0">
                            <h2 className="!text-2xl font-heading font-semibold sm:!text-3xl md:!text-4xl">
                                {t('footer.stayUpdated')}
                            </h2>
                            <p className="!mt-2 !text-sm !text-white/80 sm:!text-base">
                                {t('footer.getEarlyAccess')}
                            </p>
                        </div>

                        <div className="!flex !w-full !flex-col md:!w-1/2 md:!min-w-0">
                            <form onSubmit={handleSubmit} className="!flex !w-full !flex-col !gap-3 sm:!flex-row sm:!items-start">
                                <div className="!flex !min-w-0 !flex-1">
                                    <div className="!relative !flex !min-w-0 !flex-1">
                                        <Input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value)
                                                setEmailValidationError(null)
                                            }}
                                            placeholder={t('footer.enterEmail')}
                                            disabled={subscribing}
                                            className="!h-11 !rounded-full !border-none !bg-white/90 !pl-9 !pr-4 !text-sm !text-slate-800 !placeholder:text-slate-400 disabled:!opacity-50"
                                        />
                                        <span className="!pointer-events-none !absolute !inset-y-0 !left-3 !flex !items-center !text-slate-400">@</span>
                                    </div>
                                    {emailValidationError && (
                                        <p className="!mt-1.5 !text-sm !text-white/90">
                                            {emailValidationError}
                                        </p>
                                    )}
                                </div>
                                <Button
                                    type="submit"
                                    disabled={subscribing}
                                    className="!h-11 !rounded-full !bg-white !px-6 !text-sm font-semibold !text-[#4568DC] hover:!bg-slate-100 disabled:!opacity-50 disabled:!cursor-not-allowed sm:!shrink-0"
                                >
                                    {subscribing ? t('common.loading') : t('footer.getStarted')} {!subscribing && '→'}
                                </Button>
                            </form>
                            {subscribeSuccess && (
                                <p className="!mt-2 !text-sm !text-white/90">
                                    ✓ Successfully subscribed to our newsletter!
                                </p>
                            )}
                            {subscribeError && (
                                <p className="!mt-2 !text-sm !text-white/90">
                                    {subscribeError}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Main footer content */}
            <div className="!mx-auto !max-w-[1680px] !px-5 !py-12 sm:!px-10 md:!px-10 lg:!px-16 xl:!px-20">
                <div className="!grid !gap-10 md:!grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                    {/* Brand + social */}
                    <div className="!space-y-4">
                        <img src="/images/logo.png" alt="Airies" height={32} className="h-8" />
                        <p className="!max-w-sm !text-sm !text-slate-500">
                            {t('footer.description')}
                        </p>
                        <div className="!max-w-sm !space-y-3 !rounded-[8px] !border !border-slate-200 !bg-slate-50 !p-4 !text-sm !leading-relaxed !text-slate-600">
                            <p>
                                AIRIES is a UK and Swiss AI company helping organisations adopt artificial intelligence safely, responsibly and measurably.
                            </p>
                            <address className="!not-italic">
                                <span className="!block !font-semibold !text-slate-800">United Kingdom</span>
                                <span className="!block">AIRIES AI LTD</span>
                                <span className="!block">Suite 112, AJP Business Centre</span>
                                <span className="!block">152-154 Coles Green Road</span>
                                <span className="!block">London, NW2 6RY</span>
                                <span className="!block">United Kingdom</span>
                            </address>
                        </div>
                        <div className="!flex !gap-3">
                            <a
                                href="https://www.facebook.com/share/1FpMKkZnUg/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !border !border-slate-200 !bg-white !text-slate-600 !transition hover:!border-[#4568DC] hover:!bg-[#4568DC] hover:!text-white"
                                aria-label="Facebook"
                            >
                                <Facebook className="!h-4 !w-4" />
                            </a>
                            <a
                                href="https://www.instagram.com/airies.app?igsh=NGN0dGsyZ2o4aGww"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !border !border-slate-200 !bg-white !text-slate-600 !transition hover:!border-[#E4405F] hover:!bg-[#E4405F] hover:!text-white"
                                aria-label="Instagram"
                            >
                                <Instagram className="!h-4 !w-4" />
                            </a>
                            <a
                                href="https://www.linkedin.com/company/airiesapp/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !border !border-slate-200 !bg-white !text-slate-600 !transition hover:!border-[#0077B5] hover:!bg-[#0077B5] hover:!text-white"
                                aria-label="LinkedIn"
                            >
                                <Linkedin className="!h-4 !w-4" />
                            </a>
                            <a
                                href="https://www.tiktok.com/@airies.app?_r=1&_t=ZN-91ics4hlIEo"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group !flex !h-9 !w-9 !items-center !justify-center !rounded-full !border !border-slate-200 !bg-white !text-slate-600 !transition hover:!border-[#000000] hover:!bg-[#000000] hover:!text-white"
                                aria-label="TikTok"
                            >
                                <svg height="20" className="!fill-[#000000] group-hover:!fill-white" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg" id="fi_15069740"><path clipRule="evenodd" d="m10.5 2c0-.27614.2239-.5.5-.5h3c.2761 0 .5.22386.5.5 0 3.03757 2.4624 5.5 5.5 5.5.2761 0 .5.22386.5.5v3c0 .2761-.2239.5-.5.5-2.0494 0-3.9479-.6494-5.5-1.75317v6.25317c0 3.5899-2.9101 6.5-6.5 6.5-3.58985 0-6.5-2.9101-6.5-6.5s2.91015-6.5 6.5-6.5c.27614 0 .5.22386.5.5v3c0 .2761-.22386.5-.5.5-1.38071 0-2.5 1.1193-2.5 2.5s1.11929 2.5 2.5 2.5 2.5-1.1193 2.5-2.5zm1 .5v13.5c0 1.933-1.567 3.5-3.5 3.5s-3.5-1.567-3.5-3.5c0-1.7632 1.30385-3.2219 3-3.4646v-2.013c-2.80325.2526-5 2.6086-5 5.4776 0 3.0376 2.46243 5.5 5.5 5.5 3.0376 0 5.5-2.4624 5.5-5.5v-7.29168c0-.19721.1159-.37599.296-.45647.18-.08048.3905-.04762.5375.0839 1.3881 1.24246 3.186 2.03495 5.1665 2.14975v-2.00444c-3.1912-.24276-5.7383-2.78986-5.9811-5.98106z"></path></svg>
                            </a>
                            <a
                                href="https://x.com/airies_app?t=7ZnAlk01nIAdbLA8dS9Qyw&s=09"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group !flex !h-9 !w-9 !items-center !justify-center !rounded-full !border !border-slate-200 !bg-white !text-slate-600 !transition hover:!border-[#000000] hover:!bg-[#000000] hover:!text-white"
                                aria-label="X"
                            >
                                <svg id="fi_5968958" height="16" className="!fill-[#000000] group-hover:!fill-white" enableBackground="new 0 0 1226.37 1226.37" viewBox="0 0 1226.37 1226.37" xmlns="http://www.w3.org/2000/svg"><path d="m727.348 519.284 446.727-519.284h-105.86l-387.893 450.887-309.809-450.887h-357.328l468.492 681.821-468.492 544.549h105.866l409.625-476.152 327.181 476.152h357.328l-485.863-707.086zm-144.998 168.544-47.468-67.894-377.686-540.24h162.604l304.797 435.991 47.468 67.894 396.2 566.721h-162.604l-323.311-462.446z"></path></svg>
                            </a>
                        </div>
                    </div>

                    {/* Columns — compact layout with only main nav + legal */}
                    <div className="!grid !gap-x-8 !gap-y-8 !grid-cols-2 md:!grid-cols-2 lg:!grid-cols-2 lg:!justify-end">
                        <FooterColumn
                            title={t('footer.company')}
                            links={[
                                [t('nav.home'), '/'],
                                [t('nav.tools'), '/tools'],
                                [t('nav.universities'), '/universities'],
                                [t('nav.newsAndBlogs') || t('nav.news'), '/news'],
                                [t('nav.events'), '/events'],
                                [t('nav.pricing'), '/pricing'],
                                [t('nav.contact'), '/contact'],
                            ]}
                        />
                        {/* <FooterColumn
                            title={t('footer.resources')}
                            links={[
                                ['Academy', '/academy'],
                                ['News & Insights', '/news'],
                                ['Templates & Prompts', '/templates'],
                                ['Partner Docs', '/partners/docs'],
                                ['Innovation Hub', '/innovation'],
                            ]}
                        /> */}
                        {/* <FooterColumn
                            title={t('footer.solutions')}
                            links={[
                                ['Startups', '/solutions/startups'],
                                ['Enterprises', '/solutions/enterprises'],
                                ['Creators', '/solutions/creators'],
                                ['Family Offices', '/solutions/family-offices'],
                                ['Education', '/solutions/education'],
                            ]}
                        /> */}
                        {/* <FooterColumn
                            title={t('footer.support')}
                            links={[
                                ['Help Centre', '/help'],
                                ['FAQ', '/faq'],
                                ['Support AI (24/7)', '/support/ai'],
                                ['Report an Issue', '/support/report'],
                                ['Contact Support', '/support/contact'],
                            ]}
                        /> */}
                        {(legalLoading || legalPages.length > 0) && (
                            <FooterColumn
                                title={t('footer.legal')}
                                links={
                                    legalPages.length > 0
                                        ? [
                                              ...legalPages.map((page) => [
                                                  page.title,
                                                  `/legal/${page.slug}`,
                                              ] as [string, string]),
                                              [t('nav.faq'), '/faq/'],
                                          ]
                                        : [
                                              [t('common.loading'), '#'],
                                          ]
                                }
                                isPlaceholder={legalPages.length === 0}
                            />
                        )}
                    </div>
                </div>

                {/* Secondary rows — now 2 per row on mobile */}
                {/*<div className="!mt-12 !grid !gap-x-8 !gap-y-8 !text-sm !text-slate-500 !grid-cols-2 md:!grid-cols-3">*/}
                {/*    <FooterColumn*/}
                {/*        title="Agenda"*/}
                {/*        links={[*/}
                {/*            ['Agents', '/agenda/agents'],*/}
                {/*            ['Learning Factory', '/agenda/learning-factory'],*/}
                {/*            ['Marketplace', '/marketplace'],*/}
                {/*            ['AI Robotics & Hardware', '/agenda/hardware'],*/}
                {/*            ['Innovation Reports', '/agenda/reports'],*/}
                {/*        ]}*/}
                {/*    />*/}
                {/*    <FooterColumn*/}
                {/*        title="Events"*/}
                {/*        links={[*/}
                {/*            ['ELITE Events', '/events/elite'],*/}
                {/*            ['Request to Host / Sponsor', '/events/host'],*/}
                {/*            ['Upcoming Events', '/events'],*/}
                {/*            ['Media & Press Requests', '/events/press'],*/}
                {/*        ]}*/}
                {/*    />*/}
                {/*    <FooterColumn*/}
                {/*        title="Advertising & Partnerships"*/}
                {/*        links={[*/}
                {/*            ['Advertise', '/advertise'],*/}
                {/*            ['Sponsorship Opportunities', '/sponsorships'],*/}
                {/*            ['Media Kit Download', '/media-kit'],*/}
                {/*            ['Affiliate & Reseller Programme', '/affiliates'],*/}
                {/*        ]}*/}
                {/*    />*/}
                {/*</div>*/}

                {/* Bottom bar */}
                <div className="!mt-12 !flex !flex-col !items-center !justify-between !gap-4 !border-t !border-slate-100 !pt-4 !text-xs !text-slate-500 sm:!flex-row">
                    <div className="!flex !items-center !gap-3">
                        <LanguageSwitcher variant="footer" />

                        {/* <div className="!hidden !flex-wrap !items-center !gap-3 sm:!flex">
                            {(legalPages.length > 0
                                ? legalPages
                                : [
                                      { title: 'Legal', slug: 'privacy-policy' },
                                  ]
                            ).map((page) => (
                                <Link
                                    key={page.slug}
                                    href={`/legal/${page.slug}`}
                                    className="!hover:text-slate-700"
                                >
                                    {page.title}
                                </Link>
                            ))}
                        </div> */}
                    </div>

                    <div className="!flex !flex-col !items-center !gap-2 sm:!flex-row sm:!gap-4">
                        <p>© {new Date().getFullYear()} Airies. {t('footer.allRightsReserved')}.</p>
                        <div className="!flex !items-center !gap-1 !rounded-full !bg-emerald-50 !px-3 !py-1 !text-[11px] !text-emerald-700">
                            <span className="!h-2 !w-2 !rounded-full !bg-emerald-500" />
                            <span>{t('footer.allServicesOnline')}</span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    )
}

type FooterColumnProps = {
    title: string
    links: [string, string][]
    isPlaceholder?: boolean
}

function FooterColumn({ title, links, isPlaceholder = false }: FooterColumnProps) {
    return (
        <div>
            <h3 className="!text-xs font-semibold font-body !uppercase !tracking-wide !text-slate-500">{title}</h3>
            <ul className="!mt-3 !space-y-1.5">
                {links.map(([label, href], index) => {
                    const key = isPlaceholder ? `${title}-placeholder-${index}` : href
                    return (
                        <li key={key}>
                        {isPlaceholder ? (
                            <span className="!inline-flex !h-4 !w-32 animate-pulse rounded-full bg-slate-200" />
                        ) : (
                            <Link to={href} className="!text-sm !text-slate-500 hover:!text-slate-800">
                                {label}
                            </Link>
                        )}
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
